import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/features/auth/hooks/useAuth';
import { Rol } from '@/types/enums';

import { useProfesionalesMap } from '../hooks/useProfesionalesMap';
import type { Establecimiento, EstablecimientoListParams } from '../types';

type SortBy = NonNullable<EstablecimientoListParams['sort_by']>;
type SortOrder = NonNullable<EstablecimientoListParams['sort_order']>;

interface EstablecimientosTableProps {
  items: Establecimiento[];
  sortBy: SortBy;
  sortOrder: SortOrder;
  onSortChange: (sortBy: SortBy, sortOrder: SortOrder) => void;
  onDelete: (establecimiento: Establecimiento) => void;
  sortingDisabled?: boolean;
}

export function EstablecimientosTable({
  items,
  sortBy,
  sortOrder,
  onSortChange,
  onDelete,
  sortingDisabled = false,
}: EstablecimientosTableProps): JSX.Element {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const canDelete = currentUser?.rol === Rol.ADMINISTRADOR;
  const { getName, isLoading: profesionalesLoading } = useProfesionalesMap();

  function handleSortClick(column: SortBy): void {
    if (sortBy === column) {
      onSortChange(column, sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      onSortChange(column, 'asc');
    }
  }

  function handleRowClick(uid: string): void {
    navigate(`/establecimientos/${uid}`);
  }

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ maxHeight: { xs: 'none', md: 'calc(100vh - 260px)' } }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sortDirection={sortBy === 'nombre' ? sortOrder : false}>
              {sortingDisabled ? (
                'Nombre'
              ) : (
                <TableSortLabel
                  active={sortBy === 'nombre'}
                  direction={sortBy === 'nombre' ? sortOrder : 'asc'}
                  onClick={() => handleSortClick('nombre')}
                >
                  Nombre
                </TableSortLabel>
              )}
            </TableCell>
            <TableCell sortDirection={sortBy === 'localidad' ? sortOrder : false}>
              {sortingDisabled ? (
                'Localidad'
              ) : (
                <TableSortLabel
                  active={sortBy === 'localidad'}
                  direction={sortBy === 'localidad' ? sortOrder : 'asc'}
                  onClick={() => handleSortClick('localidad')}
                >
                  Localidad
                </TableSortLabel>
              )}
            </TableCell>
            <TableCell>Profesional asignado</TableCell>
            <TableCell align="right" sx={{ width: 120 }}>
              Acciones
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((e) => (
            <TableRow
              key={e.uid_establecimiento}
              hover
              sx={{ cursor: 'pointer' }}
              onClick={() => handleRowClick(e.uid_establecimiento)}
            >
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {e.nombre}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{e.localidad ?? '—'}</Typography>
              </TableCell>
              <TableCell>
                {profesionalesLoading ? (
                  <Skeleton variant="text" width={150} />
                ) : (
                  <Typography variant="body2">
                    {getName(e.uid_profesional_actual)}
                  </Typography>
                )}
              </TableCell>
              <TableCell align="right">
                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                  <Tooltip title="Editar">
                    <IconButton
                      size="small"
                      onClick={(ev) => {
                        ev.stopPropagation();
                        handleRowClick(e.uid_establecimiento);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {canDelete && (
                    <Tooltip title="Eliminar">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          onDelete(e);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
